import React from 'react';
import './styles.css';
import PageTitle from '../../../../uiToolkit/PageTitle';
import {analyticsText} from '../../analyticsText';
import Dropdown from '../../../../uiToolkit/Inputs/Dropdown';
import SearchInput from '../../../../uiToolkit/Inputs/SearchInput';
import Button from '../../../../uiToolkit/Buttons/Button';
import {
  AvgGradeLearnerListModel,
  AvgLearnerGradeModel,
  CourseAvgGradesModel,
  CourseWithAvgGradeModel,
} from '../../../../data/models/Gradebook/LearnerAvgGradesModel';
import OverviewGraph from '../../../../uiToolkit/Analytics/OverviewGraph';
import AnalyticsCard from '../../../../uiToolkit/Analytics/AnalyticsCard';
import AnalyticsEmptyState from '../../../../uiToolkit/Analytics/AnalyticsEmptyState';
import GradesTable, {
  GRADE_FAIL,
  GRADE_PROGRESS,
  GRADE_SUCCESS,
} from '../../components/GradesTable';
import {hasValue} from '../../../../utils/typecheck';
import TableLegend from '../../components/TableLegend';
import {truncateNumberTo2Decimals} from '../../../../utils/getTimeTextFromSeconds';
import {Tour} from '../../../../modules/Tour';

interface Props {
  search: string;
  setSearch: (value: string) => void;
  learners: AvgGradeLearnerListModel[];
  courses: CourseWithAvgGradeModel[];
  grades: {[key: string]: AvgLearnerGradeModel[]};
  coursesAnalytics?: CourseAvgGradesModel;
  orderAsc: boolean;
  setOrderAsc: (value: boolean) => void;
  setSelectedCourse: (value: string) => void;
  selectedCourse: string;
  isLoading: boolean;
  loadNextPage: () => Promise<void>;
  onExport: () => void;
}

const AnalyticsUI = React.memo(
  ({
    search,
    setSearch,
    learners,
    courses,
    grades,
    coursesAnalytics,
    orderAsc,
    setOrderAsc,
    setSelectedCourse,
    selectedCourse,
    isLoading,
    loadNextPage,
    onExport,
  }: Props) => {
    const mapCoursesForAvgGrades = () => {
      const result: any = {
        scores: {},
      };
      if (hasValue(coursesAnalytics?.globalAvgScore)) {
        result.overallAvgScore = truncateNumberTo2Decimals(
          coursesAnalytics!.globalAvgScore,
        );
      }
      courses.map(c => {
        if (hasValue(c.avgScore)) {
          result.scores[c.id] = {
            grade: c.avgScore,
            status:
              !c.isGradable || c.avgScore! >= c.passingScore
                ? GRADE_SUCCESS
                : GRADE_FAIL,
          };
        }
      });
      return result;
    };

    const mapLearnerRecords = () => {
      return learners.map(learner => ({
        id: learner.learnerId,
        name: learner.name,
        avatarUrl: learner.avatarUrl,
        scores: getLearnerGrades(learner.learnerId),
      }));
    };

    const getLearnerGrades = (learnerId: string) => {
      const scores: any = {};
      grades[learnerId]?.map(grade => {
        if (!grade.isGradable) {
          scores[grade.courseId] = {
            grade: grade.grade,
            status: GRADE_SUCCESS,
            hasStarted: grade.isStarted,
          };
        } else if (hasValue(grade.grade)) {
          scores[grade.courseId] = {
            grade: grade.grade,
            status:
              grade.grade! >= grade.passingScore ? GRADE_SUCCESS : GRADE_FAIL,
            hasStarted: grade.isStarted,
          };
        } else {
          scores[grade.courseId] = {
            status: GRADE_PROGRESS,
            hasStarted: grade.isStarted,
          };
        }
      });
      return scores;
    };

    return (
      <div className="AnalyticsContainer">
        <Tour section="analytics" />
        <div className="HeadingSection">
          <div className="GraphSection">
            <PageTitle title={analyticsText.title} />
            <OverviewGraph
              completed={coursesAnalytics?.completedCourses || 0}
              inProgress={coursesAnalytics?.inProgressCourses || 0}
              notStarted={coursesAnalytics?.notStartedCourses || 0}
              label={analyticsText.courses}
            />
          </div>
          <div className="CoursesAndCardsSection">
            <Dropdown
              options={courses.map(course => ({
                key: course.id,
                value: course.name,
              }))}
              selectedOption={selectedCourse}
              setSelectedOption={setSelectedCourse}
              placeholder={analyticsText.selectCourse}
              enableSearch
            />
            <div className="AnalyticsCardsContainer">
              <AnalyticsCard
                value={
                  <p>
                    {coursesAnalytics?.passedCourses
                      ? coursesAnalytics?.passedCourses
                      : 0}
                  </p>
                }
                label={analyticsText.passed}
              />
              <AnalyticsCard
                value={
                  <p>
                    {coursesAnalytics?.failedCourses
                      ? coursesAnalytics?.failedCourses
                      : 0}
                  </p>
                }
                label={analyticsText.failed}
              />
              {/* <AnalyticsCard
                value={
                  <p>
                    0<span className="AnalyticsCardSmallText">h</span>45
                    <span className="AnalyticsCardSmallText">m</span>
                  </p>
                }
                label={analyticsText.avgTime}
              /> */}
              <AnalyticsCard
                value={
                  <p>
                    {coursesAnalytics?.globalAvgScore &&
                    isNaN(coursesAnalytics?.globalAvgScore) ? (
                      '-'
                    ) : (
                      <>
                        {truncateNumberTo2Decimals(
                          coursesAnalytics?.globalAvgScore || 0,
                        )}
                        <span className="AnalyticsCardSmallText">%</span>
                      </>
                    )}
                  </p>
                }
                label={analyticsText.avgScore}
              />
            </div>
          </div>
        </div>
        <div className="TableLegendAndSearch">
          <TableLegend />
          <div className="SearchExport">
            <SearchInput value={search} onChange={setSearch} />
            <Button
              onClick={onExport}
              icon="icon-download_ic"
              title={analyticsText.export}
              className="ExportButton"
            />
          </div>
        </div>
        {learners.length > 0 && courses.length > 0 ? (
          <div className="AnalyticsTableContainer">
            <GradesTable
              isLoading={isLoading}
              setOrderAsc={setOrderAsc}
              orderAsc={orderAsc}
              loadNextPage={loadNextPage}
              headerItems={courses.map(c => ({
                title: c.name,
                id: c.id,
              }))}
              avgScores={mapCoursesForAvgGrades()}
              learnerRecords={mapLearnerRecords()}
            />
          </div>
        ) : (
          <AnalyticsEmptyState />
        )}
      </div>
    );
  },
);

export default AnalyticsUI;
